<template>
    <div style="display: flex; justify-content: space-between">
        <Title>Roles</Title>
        <Button
            v-if="isEditing"
            icon="pi pi-plus"
            label="Add role"
            severity="secondary"
            size="small"
            text
            @click="openRoleDialog()"
        />
    </div>
    <div v-if="!roles?.length" class="empty-state">
        <i class="pi pi-id-card empty-icon"></i>
        <div>
            <h3>No roles</h3>
            <p><i>Roles group responsibilities within your domain.</i></p>
            <Button
                v-if="isEditing"
                icon="pi pi-plus"
                label="Add role"
                severity="primary"
                text
                @click="openRoleDialog()"
            />
        </div>
    </div>
    <DataTable
        v-else
        ref="rolesTable"
        :value="roles"
        selectionMode="single"
        @row-click="
            (event: DataTableRowClickEvent) => {
                const rowLink = `role-link-${event.data.identity}`;
                rolesTable.$el.querySelector(`#${rowLink}`)?.click();
            }
        "
    >
        <Column field="name" header="Name" class="name-col">
            <template #body="{ data, field }">
                <RouterLink
                    v-if="hasValidRouteParams"
                    :id="`role-link-${data.identity}`"
                    class="role-link"
                    :to="getRoleRoute(data.identity)"
                >
                    <div>
                        {{ data[field] }}
                    </div>
                </RouterLink>
                <div v-else :id="`role-link-${data.identity}`">
                    {{ data[field] }}
                </div>
            </template>
        </Column>
        <Column field="short_description" header="Description">
            <template #body="slotProps">
                <template v-if="slotProps.data.short_description">
                    {{ slotProps.data.short_description }}
                </template>
                <span
                    v-else
                    v-tooltip.top="'A description is not (yet) available'"
                >
                    ...
                </span>
            </template>
        </Column>
        <Column field="" header="" class="buttons-col">
            <template #body="{ index }" v-if="isEditing">
                <Button
                    severity="danger"
                    type="button"
                    text
                    rounded
                    icon="pi pi-trash"
                    :loading="isLoading"
                    @click="
                        (e: MouseEvent) => {
                            // Using stopPropagation will break confirm: https://github.com/primefaces/primevue/issues/5706
                            deleteRoleEntity(e, index);
                        }
                    "
                />
            </template>
        </Column>
    </DataTable>
</template>

<script setup lang="ts">
import type { DataTableRowClickEvent } from "primevue/datatable";
import { useConfirm } from "primevue/useconfirm";
import { computed, ref } from "vue";
import { useRoute } from "vue-router";

import Title from "@/components/Title.vue";
import { useEditRoleDialog } from "@/composables/edit-role-dialog";
import { type Role } from "@/composables/role-manager";
import { useRolesManager, useRolesStore } from "@/stores/roles-store";

const { isLoading } = useRolesManager();
const { deleteRole } = useRolesStore();

const rolesTable = ref();

const props = defineProps<{
    isEditing: boolean;
    roles: Role[] | undefined;
}>();

const route = useRoute();
const confirm = useConfirm();

const hasValidRouteParams = computed(() =>
    Boolean(route.params.orgName && route.params.stekName)
);

const getRoleRoute = (roleId: string) => {
    if (!hasValidRouteParams.value) return undefined;
    return {
        name: "rolePage",
        params: {
            orgName: route.params.orgName,
            stekName: route.params.stekName,
            roleId,
        },
    };
};

// Hotkey is already assigned at ApplicationLayout
const { open: openRoleDialog } = useEditRoleDialog();

const deleteRoleEntity = async (event: Event, index: number) => {
    confirm.require({
        target: event.currentTarget as HTMLElement,
        message: `
            After deletion of the '${props.roles![index].name}' you
            cannot use it anymore. Are you sure?
        `,
        icon: "pi pi-exclamation-triangle",
        rejectProps: {
            label: "Cancel",
            severity: "secondary",
            outlined: true,
        },
        acceptProps: {
            label: "Delete",
            severity: "danger",
        },
        accept: async () => {
            await deleteRole(props.roles![index].identity);
        },
    });
};
</script>

<style scoped lang="scss">
.role-link {
    text-decoration: none;
}

:deep(.p-datatable-thead) > tr > th,
:deep(.p-datatable-tbody) > tr > td {
    &.buttons-col {
        min-width: 50px;
        text-align: right;
    }
    &.name-col {
        width: 30rem;
    }
}
:deep(.p-datatable-tbody) > tr > td.buttons-col {
    padding-top: 0;
    padding-bottom: 0;
}

.empty-state {
    display: flex;
    flex-direction: column;
    gap: 1em;
    align-items: center;
    text-align: center;

    > * {
        margin: 0;
    }

    .empty-icon {
        font-size: 3rem;
    }
}
</style>
